import React, { useState, useEffect, useRef } from "react";
import { TextField, InputAdornment, IconButton } from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import { withStyles } from "@material-ui/core/styles";
import moment from "moment";
import { Link } from "react-router-dom";
import Button from "react-md/lib/Buttons/Button";
import FontIcon from "react-md/lib/FontIcons";

import "../styles/ltrDashboard.scss";
import LTRService, { isMockMode } from "../service/LTRService";
import EventService from "service/event/EventService";
import GlobalSkeletonLoader from "components/common/GlobalSkeletonLoader";
import ColumnFilterPopover from "components/common/ColumnFilterPopover";
import filterApplied from "../../../../../assets/icons/filter-applied-icon.svg";
import funnelIcon from "../../../../../assets/icons/funnel.svg";
import eyeOrangeIcon from "../../../../../assets/icons/eye-orange.svg";
import backArrowIcon from "../../../../../assets/icons/back_left_arrow_orange.png";
import {
  STATUS_NEW_LEAD,
  STATUS_CLOSED,
  STATUS_LABEL_MAP,
  STATUS_CLASS_MAP,
  STATUS_OPTIONS,
  OUTCOME_LABEL_MAP,
  OUTCOME_ICON_MAP,
  OUTCOME_OPTIONS,
  ACTION_OPTIONS,
  ACTION_LABEL_MAP,
  ACTION_SHORT_LABEL_MAP,
  CONSENT_YES,
  CONSENT_NO,
  CONSENT_OPTIONS,
  MISCELLANEOUS_PATH,
  FROM_LTR_DASHBOARD,
} from "../constants";
// User type is the patient's profile type — the Appointments dashboard's own
// column and filter, shared as the cart dashboard shares them.
import {
  UserTypeFilter,
  getProfileIconColor,
  getProfileLabel,
} from "../../navigo-module/components/UserTypeFilter";

const ltrService = new LTRService();

// Resolved once at module load: the dashboard either talks to the API for the
// whole session or it does not, and re-checking per render would let the
// banner and the data disagree.
const MOCK_MODE = isMockMode();

// Identifiers for which filter popup is open. One at a time, backed by a
// full-screen backdrop that closes whichever is showing.
const FILTER = {
  NONE: 0,
  USER_TYPE: 1,
  PATIENT_ID: 2,
  TRIGGERED_BY: 3,
  SOURCE_DATE: 4,
  STRATIFICATION_DATE: 5,
  FOLLOW_UP_DATE: 6,
  ASSIGNEE: 7,
  ACTION: 8,
  STATUS: 9,
  LTR_ID: 10,
  TOP_DATE: 11,
  CONSENT: 12,
};

// The Consent popover's selection, reduced to the one boolean the BE filters
// on. Exclude mode keeps the options not picked; exactly one option left is
// true (Yes) or false (No), anything else is no constraint (null).
function consentFilter(mode, values) {
  const kept =
    mode === "exclude"
      ? [CONSENT_YES, CONSENT_NO].filter((v) => !values.includes(v))
      : values;
  return kept.length === 1 ? kept[0] === CONSENT_YES : null;
}

const DarkTooltip = withStyles({
  tooltip: {
    backgroundColor: "#2A2F45",
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    padding: "8px 12px",
    borderRadius: 8,
    boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
  },
  arrow: { color: "#2A2F45" },
})(Tooltip);

// Most dates in the queue carry a time that matters (a follow-up is due at an
// hour, not on a day), so they render as date over time. dateOnly drops the
// time for a value that is only a day — the source data date is stored as
// midnight UTC, which would read 5:30 AM on every row.
function DateTimeCell({ value, dateOnly = false }) {
  if (!value) return <span>-</span>;
  const m = moment(value);
  if (!m.isValid()) return <span>-</span>;
  return (
    <span className="ltr-datetime">
      <span className="ltr-datetime-date">{m.format("DD MMM YYYY")}</span>
      {!dateOnly && <span className="ltr-datetime-time">{m.format("h:mm A")}</span>}
    </span>
  );
}

function LTRDashboard(props) {
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(20);
  const [componentMounted, setComponentMounted] = useState(false);

  const [openFilter, setOpenFilter] = useState(FILTER.NONE);
  const [openBackdrop, setOpenBackdrop] = useState(false);

  // Multi-select filters carry a `mode` ("include" | "exclude") alongside the
  // chosen ids. Mode flips when the user clicks "Invert" in the popover, which
  // lets "everything except these few" travel without enumerating the dataset.
  // The patient profile types picked in the User type popup; empty = no filter.
  const [profileTypesFilter, setProfileTypesFilter] = useState([]);

  const [triggeredByFilterValue, setTriggeredByFilterValue] = useState([]);
  const [triggeredByFilterMode, setTriggeredByFilterMode] = useState("include");

  const [assigneeFilterValue, setAssigneeFilterValue] = useState([]);
  const [assigneeFilterMode, setAssigneeFilterMode] = useState("include");

  const [actionFilterValue, setActionFilterValue] = useState([]);
  const [actionFilterMode, setActionFilterMode] = useState("include");

  const [statusFilterValue, setStatusFilterValue] = useState([]);
  const [statusFilterMode, setStatusFilterMode] = useState("include");

  const [outcomeFilterValue, setOutcomeFilterValue] = useState([]);

  const [consentFilterValue, setConsentFilterValue] = useState([]);
  const [consentFilterMode, setConsentFilterMode] = useState("include");

  const [patientIdFilterText, setPatientIdFilterText] = useState("");
  const [appliedPatientId, setAppliedPatientId] = useState("");

  const [ltrIdFilterText, setLtrIdFilterText] = useState("");
  const [appliedLtrId, setAppliedLtrId] = useState("");

  const [sourceDateFrom, setSourceDateFrom] = useState("");
  const [sourceDateTo, setSourceDateTo] = useState("");
  const [stratDateFrom, setStratDateFrom] = useState("");
  const [stratDateTo, setStratDateTo] = useState("");
  const [followUpFrom, setFollowUpFrom] = useState("");
  const [followUpTo, setFollowUpTo] = useState("");

  // Derive the filled-funnel state from the dates themselves so the icon
  // reflects whichever popup applied the range (top pill or column header).
  const sourceDateFilter = !!(sourceDateFrom && sourceDateTo);
  const stratDateFilter = !!(stratDateFrom && stratDateTo);
  const followUpFilter = !!(followUpFrom && followUpTo);

  const triggeredByIconRef = useRef(null);
  const assigneeIconRef = useRef(null);
  const actionIconRef = useRef(null);
  const statusIconRef = useRef(null);
  const consentIconRef = useRef(null);

  const [stats, setStats] = useState({
    totalCases: 0,
    newLeads: 0,
    newLeadsWithoutConsent: 0,
    inProgress: 0,
    closed: 0,
    closedSuccessfully: 0,
    closedUnsuccessfully: 0,
    userNotInterested: 0,
    falsePositive: 0,
  });

  const navigateToDetail = (record) => {
    if (!record || !props.history) return;
    const id = record.id || record.ID || record._id;
    if (!id) return;
    // The case page's back arrow steps back to this entry instead of pushing
    // the dashboard again (see LTRDetail goBack).
    props.history.push(`/miscellaneous/ltr/details/${id}`, { [FROM_LTR_DASHBOARD]: true });
  };

  const closeFilters = () => {
    setOpenFilter(FILTER.NONE);
    setOpenBackdrop(false);
  };

  const openFilterPopup = (which) => {
    setOpenBackdrop(true);
    setOpenFilter(which);
  };

  const hasActiveFilters =
    profileTypesFilter.length > 0 ||
    triggeredByFilterValue.length > 0 ||
    assigneeFilterValue.length > 0 ||
    actionFilterValue.length > 0 ||
    statusFilterValue.length > 0 ||
    outcomeFilterValue.length > 0 ||
    consentFilterValue.length > 0 ||
    !!appliedPatientId ||
    !!appliedLtrId ||
    sourceDateFilter ||
    stratDateFilter ||
    followUpFilter;

  // Resets every filter in a single batched render. The consolidated effect
  // below sees the deps change and refetches once with the cleared body.
  const resetAllFilters = () => {
    setProfileTypesFilter([]);
    setTriggeredByFilterValue([]);
    setTriggeredByFilterMode("include");
    setAssigneeFilterValue([]);
    setAssigneeFilterMode("include");
    setActionFilterValue([]);
    setActionFilterMode("include");
    setStatusFilterValue([]);
    setStatusFilterMode("include");
    setOutcomeFilterValue([]);
    setConsentFilterValue([]);
    setConsentFilterMode("include");
    setPatientIdFilterText("");
    setAppliedPatientId("");
    setLtrIdFilterText("");
    setAppliedLtrId("");
    setSourceDateFrom("");
    setSourceDateTo("");
    setStratDateFrom("");
    setStratDateTo("");
    setFollowUpFrom("");
    setFollowUpTo("");
    setPageNum(1);
  };

  useEffect(() => {
    fetchDashboard();
    setComponentMounted(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The parent bumps refreshToken when the user returns from a detail page
  // where they actually saved something. Refetch with the current filters
  // intact so stale rows update without disturbing pagination or filter state.
  useEffect(() => {
    if (!componentMounted) return;
    if (!props.refreshToken) return;
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.refreshToken]);

  // Single source of truth for filter-driven fetches. Every applied filter is a
  // dep, so clearing one fires this effect and the fetch runs with up-to-date
  // values (no stale-closure trap from inline fetches). Date ranges are skipped
  // while half-set so a partial pick doesn't round-trip.
  useEffect(() => {
    if (!componentMounted) return;
    if (!!sourceDateFrom !== !!sourceDateTo) return;
    if (!!stratDateFrom !== !!stratDateTo) return;
    if (!!followUpFrom !== !!followUpTo) return;
    fetchDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pageNum,
    profileTypesFilter,
    triggeredByFilterValue,
    assigneeFilterValue,
    actionFilterValue,
    statusFilterValue,
    outcomeFilterValue,
    consentFilterValue,
    consentFilterMode,
    appliedPatientId,
    appliedLtrId,
    sourceDateFrom,
    sourceDateTo,
    stratDateFrom,
    stratDateTo,
    followUpFrom,
    followUpTo,
  ]);

  const loadAssigneeOptions = async (q, offset, limit) => {
    const res = await ltrService.fetchAssignees({ q, offset, limit });
    return res || { options: [], total: 0, hasMore: false };
  };

  const loadTriggerReasonOptions = async (q, offset, limit) => {
    const res = await ltrService.fetchTriggerReasons({ q, offset, limit });
    return res || { options: [], total: 0, hasMore: false };
  };

  // Wraps a multi-select filter in the {mode, values} shape the BE expects.
  // Returns null when there are no values, so the caller omits that filter from
  // the body and empty filters never travel on the wire.
  const filterClause = (mode, values) => {
    if (!values || values.length === 0) return null;
    return { mode, values };
  };

  // A date input gives a calendar day; the BE matches a closed instant range.
  // Expand the day to its full local span so a case stratified at 6pm on the
  // "to" date is inside the range rather than just outside it.
  const dayRange = (from, to) => ({
    from: moment(from).startOf("day").toISOString(),
    to: moment(to).endOf("day").toISOString(),
  });

  const buildFilterBody = ({ paginate } = { paginate: true }) => {
    const body = paginate ? { pageNum, pageSize } : {};

    // Same shape as the Appointments filter: a plain list, dropped when empty.
    if (profileTypesFilter.length > 0) body.profileTypes = profileTypesFilter;

    const riskLevelTriggeredBy = filterClause(
      triggeredByFilterMode,
      triggeredByFilterValue.map((o) => o.value),
    );
    if (riskLevelTriggeredBy) body.riskLevelTriggeredBy = riskLevelTriggeredBy;

    const assigneeIds = filterClause(
      assigneeFilterMode,
      assigneeFilterValue.map((o) => Number(o.value)),
    );
    if (assigneeIds) body.assigneeIds = assigneeIds;

    const actions = filterClause(
      actionFilterMode,
      actionFilterValue.map((o) => Number(o.value)),
    );
    if (actions) body.actions = actions;

    const statuses = filterClause(
      statusFilterMode,
      statusFilterValue.map((o) => Number(o.value)),
    );
    if (statuses) body.statuses = statuses;

    // Outcomes share the status popover, so they share its mode too —
    // inverting the selection must invert both halves or "everything except
    // false positives" would silently become "only false positives".
    const outcomes = filterClause(
      statusFilterMode,
      outcomeFilterValue.map((o) => Number(o.value)),
    );
    if (outcomes) body.outcomes = outcomes;

    const consentGiven = consentFilter(
      consentFilterMode,
      consentFilterValue.map((o) => o.value),
    );
    if (consentGiven !== null) body.consentGiven = consentGiven;

    if (sourceDateFilter) body.sourceDataDate = dayRange(sourceDateFrom, sourceDateTo);
    if (stratDateFilter)
      body.riskStratificationDate = dayRange(stratDateFrom, stratDateTo);
    if (followUpFilter) body.followUpDate = dayRange(followUpFrom, followUpTo);

    if (appliedPatientId.trim()) {
      body.patientId = parseInt(appliedPatientId.trim(), 10);
    }
    if (appliedLtrId.trim()) {
      body.ltrId = appliedLtrId.trim();
    }

    return body;
  };

  const fetchDashboard = () => {
    setLoading(true);
    ltrService.fetchDashboard(
      buildFilterBody({ paginate: true }),
      (res) => {
        const data = res.data || res;
        setRecords(data.response || []);
        setTotalCount(data.totalCount || 0);
        fetchStats();
        setLoading(false);
      },
      (err) => {
        console.error("Error fetching LTR dashboard:", err);
        EventService.emit("showError", {
          message: "Failed to fetch LTR cases",
        });
        setLoading(false);
      },
    );
  };

  const fetchStats = () => {
    ltrService.fetchDashboardStats(
      buildFilterBody({ paginate: false }),
      (res) => {
        const data = res.data || res;
        setStats({
          totalCases: data.totalCases || 0,
          newLeads: data.newLeads || 0,
          newLeadsWithoutConsent: data.newLeadsWithoutConsent || 0,
          inProgress: data.inProgress || 0,
          closed: data.closed || 0,
          closedSuccessfully: data.closedSuccessfully || 0,
          closedUnsuccessfully: data.closedUnsuccessfully || 0,
          userNotInterested: data.userNotInterested || 0,
          falsePositive: data.falsePositive || 0,
        });
      },
      (err) => {
        console.error("Error fetching LTR dashboard stats:", err);
      },
    );
  };

  // Renders one of the three inline date-range popups used by the date columns.
  const renderDatePopup = (from, setFrom, to, setTo) => (
    <div className="ltr-filter-popup" onClick={(e) => e.stopPropagation()}>
      <div className="ltr-filter-date-row">
        <label>From</label>
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
      </div>
      <div className="ltr-filter-date-row">
        <label>To</label>
        <input
          type="date"
          value={to}
          min={from || undefined}
          onChange={(e) => {
            const next = e.target.value;
            if (from && next && next >= from) {
              setTo(next);
              closeFilters();
              setPageNum(1);
            } else if (!from) {
              setTo(next);
            }
          }}
        />
      </div>
      {from && !to && <p className="ltr-filter-hint">Now select end date</p>}
      <Button
        flat
        className="ltr-filter-clear-btn"
        onClick={() => {
          setFrom("");
          setTo("");
          setPageNum(1);
        }}
      >
        Clear
      </Button>
    </div>
  );

  // Renders one of the two free-text search popups (LTR id, Patient id).
  const renderSearchPopup = ({
    placeholder,
    text,
    setText,
    applied,
    setApplied,
    numericOnly,
  }) => (
    <div
      className="ltr-filter-popup ltr-filter-search-popup"
      onClick={(e) => e.stopPropagation()}
    >
      <TextField
        placeholder={placeholder}
        value={text}
        onChange={(e) => {
          let val = e.target.value;
          if (numericOnly) val = val.replace(/[^0-9]/g, "");
          if (val.length > 20) val = val.slice(0, 20);
          setText(val);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            setApplied(text);
            setPageNum(1);
            closeFilters();
          }
        }}
        InputProps={{
          endAdornment: text ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => {
                  setText("");
                  setApplied("");
                  setPageNum(1);
                }}
              >
                <FontIcon className="ltr-filter-clear-icon">close</FontIcon>
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
      />
      <Button
        flat
        className="ltr-filter-apply-btn"
        onClick={() => {
          setApplied(text);
          setPageNum(1);
          closeFilters();
        }}
      >
        Apply
      </Button>
    </div>
  );

  const numFrom = totalCount === 0 ? 0 : (pageNum - 1) * pageSize + 1;
  const numTo = Math.min(pageNum * pageSize, totalCount);

  return (
    <div className="ltr-dashboard-container">
      {/* Header */}
      <div className="ltr-dashboard-header">
        <img
          src={backArrowIcon}
          alt="back"
          className="ltr-dashboard-back-btn"
          onClick={() => props.history && props.history.push(MISCELLANEOUS_PATH)}
        />
        <h1>LTR dashboard</h1>
      </div>

      {/* Controls */}
      <div className="ltr-dashboard-controls">
        <div className="ltr-dashboard-date-filter">
          <div
            className="ltr-date-range-picker"
            onClick={() => {
              if (!stratDateFrom && !stratDateTo) {
                openFilterPopup(FILTER.TOP_DATE);
              }
            }}
          >
            <FontIcon className="ltr-date-calendar-icon">date_range</FontIcon>
            {stratDateFrom && stratDateTo ? (
              <>
                <span className="ltr-date-selected-text">
                  {moment(stratDateFrom).format("DD MMM YYYY")} –{" "}
                  {moment(stratDateTo).format("DD MMM YYYY")}
                </span>
                <span
                  className="ltr-date-clear-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setStratDateFrom("");
                    setStratDateTo("");
                    setPageNum(1);
                  }}
                >
                  ✕
                </span>
              </>
            ) : (
              <span className="ltr-date-placeholder">Filter by date</span>
            )}
          </div>
          {openFilter === FILTER.TOP_DATE &&
            renderDatePopup(
              stratDateFrom,
              setStratDateFrom,
              stratDateTo,
              setStratDateTo,
            )}
        </div>
      </div>

      {/* Sample-data banner. Deliberately loud and not dismissible — it is the
          safeguard that stops mock mode reaching production unnoticed. */}
      {MOCK_MODE && (
        <div className="ltr-mock-banner">
          <FontIcon className="ltr-mock-banner-icon">science</FontIcon>
          <span>
            <strong>Showing sample data.</strong> The LTR service is not being
            called. Add <code>?mock=0</code> to the URL to use the real API.
          </span>
        </div>
      )}

      {/* Stat tiles. Counts are computed over the whole filtered dataset, not
          just this page, so they don't shift as the user pages through. */}
      <div className="ltr-dashboard-stats">
        <div className="ltr-stat-card">
          <div className="ltr-stat-label">Total LTR cases</div>
          <div className="ltr-stat-value">{stats.totalCases}</div>
        </div>
        <div className="ltr-stat-card">
          <div className="ltr-stat-label">New leads</div>
          <div className="ltr-stat-value new-lead">{stats.newLeads}</div>
          {/* Leads without contact consent are not auto-assigned to a doctor,
              so someone has to pick them up by hand. */}
          {stats.newLeadsWithoutConsent > 0 && (
            <div className="ltr-stat-sub">{stats.newLeadsWithoutConsent} without consent</div>
          )}
        </div>
        <div className="ltr-stat-card">
          <div className="ltr-stat-label">In progress</div>
          <div className="ltr-stat-value in-progress">{stats.inProgress}</div>
        </div>
        <div className="ltr-stat-card">
          <div className="ltr-stat-label">Closed</div>
          <div className="ltr-stat-value closed">{stats.closed}</div>
        </div>
        <div className="ltr-stat-card">
          <div className="ltr-stat-label">Closed successfully</div>
          <div className="ltr-stat-value successful">
            {stats.closedSuccessfully}
          </div>
        </div>
        <div className="ltr-stat-card">
          <div className="ltr-stat-label">Closed unsuccessfully</div>
          <div className="ltr-stat-value unsuccessful">
            {stats.closedUnsuccessfully}
          </div>
        </div>
        <div className="ltr-stat-card">
          <div className="ltr-stat-label">User not interested</div>
          <div className="ltr-stat-value not-interested">
            {stats.userNotInterested}
          </div>
        </div>
        <div className="ltr-stat-card">
          <div className="ltr-stat-label">False positive</div>
          <div className="ltr-stat-value false-positive">
            {stats.falsePositive}
          </div>
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <GlobalSkeletonLoader />
      ) : (
        <div className="ltr-dashboard-table-container">
          <table>
            <thead>
              <tr>
                <th className="filter-th">
                  <span>LTR ID</span>
                  <img
                    src={appliedLtrId ? filterApplied : funnelIcon}
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.LTR_ID)}
                  />
                  {openFilter === FILTER.LTR_ID &&
                    renderSearchPopup({
                      placeholder: "LTR ID",
                      text: ltrIdFilterText,
                      setText: setLtrIdFilterText,
                      applied: appliedLtrId,
                      setApplied: setAppliedLtrId,
                      numericOnly: false,
                    })}
                </th>

                <th className="filter-th">
                  <span>User type</span>
                  <img
                    src={profileTypesFilter.length ? filterApplied : funnelIcon}
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.USER_TYPE)}
                  />
                  {openFilter === FILTER.USER_TYPE && (
                    <UserTypeFilter
                      selected={profileTypesFilter}
                      onClose={closeFilters}
                      onApply={(profileTypes) => {
                        setProfileTypesFilter(profileTypes);
                        setPageNum(1);
                        closeFilters();
                      }}
                    />
                  )}
                </th>

                <th className="filter-th">
                  <span>Consent</span>
                  <img
                    ref={consentIconRef}
                    src={consentFilterValue.length ? filterApplied : funnelIcon}
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.CONSENT)}
                  />
                  {openFilter === FILTER.CONSENT && (
                    <ColumnFilterPopover
                      anchorRef={consentIconRef}
                      title="Filter by Consent"
                      searchPlaceholder="Search..."
                      options={CONSENT_OPTIONS.map((o) => ({
                        id: o.value,
                        label: o.label,
                      }))}
                      selectedIds={consentFilterValue.map((o) => o.value)}
                      mode={consentFilterMode}
                      onApply={(ids, items, mode) => {
                        setConsentFilterValue(
                          CONSENT_OPTIONS.filter((o) => ids.includes(o.value)),
                        );
                        setConsentFilterMode(mode);
                        setPageNum(1);
                        closeFilters();
                      }}
                      onClose={closeFilters}
                    />
                  )}
                </th>

                <th className="filter-th">
                  <span>Patient ID</span>
                  <img
                    src={appliedPatientId ? filterApplied : funnelIcon}
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.PATIENT_ID)}
                  />
                  {openFilter === FILTER.PATIENT_ID &&
                    renderSearchPopup({
                      placeholder: "Patient ID",
                      text: patientIdFilterText,
                      setText: setPatientIdFilterText,
                      applied: appliedPatientId,
                      setApplied: setAppliedPatientId,
                      numericOnly: true,
                    })}
                </th>

                <th className="filter-th">
                  <span>Risk level triggered by</span>
                  <img
                    ref={triggeredByIconRef}
                    src={
                      triggeredByFilterValue.length ? filterApplied : funnelIcon
                    }
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.TRIGGERED_BY)}
                  />
                  {openFilter === FILTER.TRIGGERED_BY && (
                    <ColumnFilterPopover
                      anchorRef={triggeredByIconRef}
                      title="Filter by Risk Level Triggered By"
                      searchPlaceholder="Search triggers..."
                      loadOptions={loadTriggerReasonOptions}
                      selectedIds={triggeredByFilterValue.map((o) => o.value)}
                      selectedItems={triggeredByFilterValue.map((o) => ({
                        id: o.value,
                        label: o.label,
                      }))}
                      mode={triggeredByFilterMode}
                      onApply={(ids, items, mode) => {
                        setTriggeredByFilterValue(
                          items.map((it) => ({
                            value: it.id,
                            label: it.label,
                          })),
                        );
                        setTriggeredByFilterMode(mode);
                        setPageNum(1);
                        closeFilters();
                      }}
                      onClose={closeFilters}
                    />
                  )}
                </th>

                <th className="filter-th">
                  <span>Source data date</span>
                  <img
                    src={sourceDateFilter ? filterApplied : funnelIcon}
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.SOURCE_DATE)}
                  />
                  {openFilter === FILTER.SOURCE_DATE &&
                    renderDatePopup(
                      sourceDateFrom,
                      setSourceDateFrom,
                      sourceDateTo,
                      setSourceDateTo,
                    )}
                </th>

                <th className="filter-th">
                  <span>Risk stratification date</span>
                  <img
                    src={stratDateFilter ? filterApplied : funnelIcon}
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.STRATIFICATION_DATE)}
                  />
                  {openFilter === FILTER.STRATIFICATION_DATE &&
                    renderDatePopup(
                      stratDateFrom,
                      setStratDateFrom,
                      stratDateTo,
                      setStratDateTo,
                    )}
                </th>

                <th className="filter-th">
                  <span>Follow up date</span>
                  <img
                    src={followUpFilter ? filterApplied : funnelIcon}
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.FOLLOW_UP_DATE)}
                  />
                  {openFilter === FILTER.FOLLOW_UP_DATE &&
                    renderDatePopup(
                      followUpFrom,
                      setFollowUpFrom,
                      followUpTo,
                      setFollowUpTo,
                    )}
                </th>

                <th className="filter-th">
                  <span>Assigned to</span>
                  <img
                    ref={assigneeIconRef}
                    src={assigneeFilterValue.length ? filterApplied : funnelIcon}
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.ASSIGNEE)}
                  />
                  {openFilter === FILTER.ASSIGNEE && (
                    <ColumnFilterPopover
                      anchorRef={assigneeIconRef}
                      title="Filter by Assignee"
                      searchPlaceholder="Search people..."
                      loadOptions={loadAssigneeOptions}
                      selectedIds={assigneeFilterValue.map((o) => o.value)}
                      selectedItems={assigneeFilterValue.map((o) => ({
                        id: o.value,
                        label: o.label,
                      }))}
                      mode={assigneeFilterMode}
                      onApply={(ids, items, mode) => {
                        setAssigneeFilterValue(
                          items.map((it) => ({
                            value: it.id,
                            label: it.label,
                          })),
                        );
                        setAssigneeFilterMode(mode);
                        setPageNum(1);
                        closeFilters();
                      }}
                      onClose={closeFilters}
                    />
                  )}
                </th>

                <th>
                  <span>Calls</span>
                </th>

                <th className="filter-th">
                  <span>Action</span>
                  <img
                    ref={actionIconRef}
                    src={actionFilterValue.length ? filterApplied : funnelIcon}
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.ACTION)}
                  />
                  {openFilter === FILTER.ACTION && (
                    <ColumnFilterPopover
                      anchorRef={actionIconRef}
                      title="Filter by Action"
                      searchPlaceholder="Search actions..."
                      options={ACTION_OPTIONS.map((o) => ({
                        id: o.value,
                        label: o.label,
                      }))}
                      selectedIds={actionFilterValue.map((o) => o.value)}
                      mode={actionFilterMode}
                      onApply={(ids, items, mode) => {
                        setActionFilterValue(
                          ACTION_OPTIONS.filter((o) => ids.includes(o.value)),
                        );
                        setActionFilterMode(mode);
                        setPageNum(1);
                        closeFilters();
                      }}
                      onClose={closeFilters}
                    />
                  )}
                </th>

                <th className="filter-th">
                  <span>Status</span>
                  <img
                    ref={statusIconRef}
                    src={
                      statusFilterValue.length || outcomeFilterValue.length
                        ? filterApplied
                        : funnelIcon
                    }
                    alt="filter"
                    className="filter-icon"
                    onClick={() => openFilterPopup(FILTER.STATUS)}
                  />
                  {openFilter === FILTER.STATUS && (
                    <ColumnFilterPopover
                      anchorRef={statusIconRef}
                      title="Filter by Status"
                      searchPlaceholder="Search statuses..."
                      // Statuses and closure outcomes share one popover
                      // because that is how the column reads: a closed case
                      // shows its outcome inside the Closed pill. Outcome ids
                      // are offset by 100 on the wire so the two sets never
                      // collide, then split back apart on apply.
                      options={[
                        ...STATUS_OPTIONS.map((o) => ({
                          id: o.value,
                          label: o.label,
                        })),
                        ...OUTCOME_OPTIONS.map((o) => ({
                          id: o.value + 100,
                          label: o.label,
                        })),
                      ]}
                      selectedIds={[
                        ...statusFilterValue.map((o) => o.value),
                        ...outcomeFilterValue.map((o) => o.value + 100),
                      ]}
                      mode={statusFilterMode}
                      onApply={(ids, items, mode) => {
                        setStatusFilterValue(
                          STATUS_OPTIONS.filter((o) => ids.includes(o.value)),
                        );
                        setOutcomeFilterValue(
                          OUTCOME_OPTIONS.filter((o) =>
                            ids.includes(o.value + 100),
                          ),
                        );
                        setStatusFilterMode(mode);
                        setPageNum(1);
                        closeFilters();
                      }}
                      onClose={closeFilters}
                    />
                  )}
                </th>

                <th>
                  <button
                    className="reset-filters-btn"
                    onClick={resetAllFilters}
                    disabled={!hasActiveFilters}
                  >
                    <span className="reset-filters-label">Reset filters</span>
                    <FontIcon className="reset-filters-icon">close</FontIcon>
                  </button>
                </th>
              </tr>
            </thead>

            <tbody>
              {records.length === 0 && (
                <tr className="no-records-row">
                  <td className="no-records-cell">No records found</td>
                </tr>
              )}
              {records.map((record, index) => {
                const status = record.status;
                const statusClass = STATUS_CLASS_MAP[status] || "new-lead";
                const isClosed = status === STATUS_CLOSED;
                const outcome = record.closureOutcome;
                const actions = Array.isArray(record.actions)
                  ? record.actions
                  : [];

                return (
                  <tr
                    key={record.id || index}
                    className={isClosed ? "ltr-row-closed" : ""}
                  >
                    <td className="ltr-id-cell">{record.ltrId || "-"}</td>

                    <td>
                      {/* Absent only when user-service could not be read. */}
                      {record.patientProfileType != null ? (
                        <DarkTooltip
                          arrow
                          placement="top"
                          title={getProfileLabel(record.patientProfileType)}
                        >
                          <span
                            className="ltr-user-type"
                            style={{
                              backgroundColor: getProfileIconColor(
                                record.patientProfileType,
                              ),
                            }}
                          >
                            <FontIcon className="ltr-user-type-icon">
                              person
                            </FontIcon>
                          </span>
                        </DarkTooltip>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>
                      {/* Without consent the case was not routed to a doctor,
                          which is why its assignee and follow-up are empty. */}
                      <span
                        className={`ltr-consent-pill ${record.consentGiven ? "yes" : "no"}`}
                      >
                        {record.consentGiven ? "Yes" : "No"}
                      </span>
                    </td>

                    <td>
                      {record.patientId ? (
                        <Link to={`/patient-record/${record.patientId}`} className="ltr-patient-link">
                          {record.patientId}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td className="ltr-trigger-cell">
                      {Array.isArray(record.riskLevelTriggeredBy) &&
                      record.riskLevelTriggeredBy.length > 0
                        ? record.riskLevelTriggeredBy.join(", ")
                        : "-"}
                    </td>

                    <td>
                      <DateTimeCell value={record.sourceDataDate} dateOnly />
                    </td>
                    <td>
                      <DateTimeCell value={record.riskStratificationDate} />
                    </td>
                    <td>
                      <DateTimeCell value={record.followUpDate} />
                    </td>

                    <td>
                      {record.assignedToName ? (
                        <span className="ltr-assignee">
                          {record.assignedToName}
                        </span>
                      ) : (
                        "-"
                      )}
                    </td>

                    <td>{record.callCount || 0}</td>

                    <td className="ltr-action-cell">
                      {actions.length === 0 ? (
                        // A New lead has had no call yet, so there is nothing
                        // to set an action against — the CTA only appears once
                        // the case is actually being worked.
                        status === STATUS_NEW_LEAD ? (
                          <span className="ltr-action-empty">—</span>
                        ) : (
                          <button
                            className="ltr-set-action-btn"
                            onClick={() => navigateToDetail(record)}
                          >
                            + Set action
                          </button>
                        )
                      ) : (
                        <DarkTooltip
                          arrow
                          placement="top"
                          title={actions
                            .map((a) => ACTION_LABEL_MAP[a] || a)
                            .join(", ")}
                        >
                          <span className="ltr-action-pill">
                            <span className="ltr-action-label">
                              {ACTION_SHORT_LABEL_MAP[actions[0]] || "-"}
                            </span>
                            {actions.length > 1 && (
                              <span className="ltr-action-more">
                                +{actions.length - 1}
                              </span>
                            )}
                          </span>
                        </DarkTooltip>
                      )}
                    </td>

                    <td>
                      <span className={`ltr-status-pill ${statusClass}`}>
                        {isClosed && OUTCOME_ICON_MAP[outcome] && (
                          <DarkTooltip
                            arrow
                            placement="top"
                            title={OUTCOME_LABEL_MAP[outcome] || "Closed"}
                          >
                            <FontIcon className="ltr-status-outcome-icon">
                              {OUTCOME_ICON_MAP[outcome]}
                            </FontIcon>
                          </DarkTooltip>
                        )}
                        {STATUS_LABEL_MAP[status] || "-"}
                      </span>
                    </td>

                    <td>
                      <button
                        className="view-btn"
                        onClick={() => navigateToDetail(record)}
                      >
                        <img
                          src={eyeOrangeIcon}
                          alt="view"
                          style={{ width: 16, height: 16 }}
                        />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Pagination */}
          <div className="ltr-dashboard-pagination">
            <Button
              className="pagination-btn"
              disabled={pageNum <= 1 || loading}
              onClick={() => setPageNum(pageNum - 1)}
            >
              <FontIcon className="pagination-icon">chevron_left</FontIcon>
            </Button>
            <div className="pagination-info">
              Showing&nbsp;
              <span className="pagination-range">
                {numFrom}&nbsp;-&nbsp;{numTo}
              </span>
              <span>&nbsp;of&nbsp;</span>
              <span>{totalCount}</span>
            </div>
            <Button
              className="pagination-btn"
              disabled={loading || pageNum * pageSize >= totalCount}
              onClick={() => setPageNum(pageNum + 1)}
            >
              <FontIcon className="pagination-icon">chevron_right</FontIcon>
            </Button>
          </div>
        </div>
      )}

      {openBackdrop && (
        <div className="ltr-filter-backdrop" onClick={closeFilters}></div>
      )}
    </div>
  );
}

export default LTRDashboard;
